import { useEffect, useState } from "react";

/**
 * ServiceNameTooltip
 *
 * Renders a single `position: fixed` tooltip for the service/test-name cells so
 * it is NEVER clipped by a table's horizontal scroll box, while the table can
 * keep `overflow-x: auto` for horizontal scrolling on reduced screens.
 *
 * It uses document-level event delegation, so it works for every table/tab on a
 * page without editing each inline `.testNames` block: on hover it reads the
 * hidden `.testNamesList` markup from the cell and clones it into a floating
 * tooltip anchored to the cell (flips above when near the viewport bottom).
 *
 * @param {object} props
 * @param {object} props.styles - the CSS-module object that defines the
 *   `.testNames` / `.testNamesList` classes (pass the page's imported styles).
 */
const ServiceNameTooltip = ({ styles }) => {
  const [tip, setTip] = useState(null);

  useEffect(() => {
    const cls = styles?.["testNames"];
    const listCls = styles?.["testNamesList"];
    if (!cls || !listCls) return undefined;

    const showFor = (host) => {
      const listEl = host.querySelector("." + listCls);
      if (!listEl) return;
      const html = listEl.innerHTML;
      if (!html || !html.trim()) return;
      const rect = host.getBoundingClientRect();
      const flipUp = window.innerHeight - rect.bottom < 200;
      setTip({
        html,
        x: rect.left,
        y: flipUp ? rect.top - 8 : rect.bottom + 4,
        flipUp,
      });
    };

    const onOver = (e) => {
      const host = e.target.closest("." + cls);
      if (host) showFor(host);
    };
    const onOut = (e) => {
      const host = e.target.closest("." + cls);
      if (host && !host.contains(e.relatedTarget)) setTip(null);
    };
    const onScroll = () => setTip(null);

    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [styles]);

  if (!tip) return null;

  return (
    <div
      className={styles["testNamesList"]}
      style={{
        position: "fixed",
        left: tip.x,
        top: tip.y,
        display: "block",
        zIndex: 99999,
        pointerEvents: "none",
        transform: tip.flipUp ? "translateY(-100%)" : "none",
      }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: tip.html }}
    />
  );
};

export default ServiceNameTooltip;
